import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import SEO from '@components/common/SEO/SEO';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import LandingPageContainer from '@layouts/LandingPageLayout/LandingPageContainer';
import ToolLandingLayout from '@layouts/LandingPageLayout/ToolLandingLayout';
import { getToolsPage } from '@lib/API';
import { SET_OS, SET_PLATFORM } from '@store/device-state';
import { RESET_EDITOR_STATE } from '@store/editor-state/editorState';
import { useAppSelector } from '@store/hook';
import { RESET_PLAYER_STATE } from '@store/player-state/playerState';
import { CLOSE_MODAL } from '@store/shell-state/shellState';
import {
  getUploadFileDetails,
  RESET_UPLOAD_STATE,
} from '@store/upload-state/upload-state';
import { userStateSelector } from '@store/user-state/UserState';
import { filterBlogResponseForCards } from '@utils/commonUtils';
import { generateJSONLDSchema, generateJSONLDSchemaHowTo } from '@utils/seo';
import { RootProps } from 'src/pages/_app';
import { ToolsLandingPageResponse } from '../../../types/landing-page';

interface ToolProps extends RootProps {
  toolsPageState: ToolsLandingPageResponse;
  allowEditor: boolean;
}

function ToolLandingPage(props: ToolProps) {
  const { toolsPageState, os, socialLinks, platform, allowEditor, browser } =
    props;

  const { FAQItems, SEOSection, HowToSchema } = toolsPageState;

  const dispatch = useDispatch();
  const router = useRouter();

  const user = useAppSelector(userStateSelector);
  const uploadState = useAppSelector(getUploadFileDetails);

  const LDschema = generateJSONLDSchema(
    {
      Title: SEOSection.Title,
      Description: SEOSection.Description,
      faqItems: FAQItems,
    },
    SEOSection.CanonicalURL,
    'WebPage',
  );

  useEffect(() => {
    dispatch(RESET_EDITOR_STATE());
    dispatch(RESET_PLAYER_STATE());
    dispatch(RESET_UPLOAD_STATE());
    dispatch(CLOSE_MODAL());
  }, [dispatch]);

  useEffect(() => {
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      dispatch(SET_PLATFORM(PlatformType.Tablet));
    } else {
      dispatch(SET_PLATFORM(platform));
    }
    dispatch(SET_OS(os));

    // Cancel the upload if user navigates backs to LP
    if (uploadState.cancelToken) {
      uploadState.cancelToken('Cancelled Previous upload');
    }
  }, []);

  return (
    <ToolLandingLayout>
      <SEO
        title={SEOSection.Title}
        description={SEOSection.Description}
        canonicalurl={SEOSection.CanonicalURL}
        keywords={SEOSection.Keywords}
        robotIndex={SEOSection.RobotIndex}
        robotFollow={SEOSection.RobotFollow}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(LDschema)}`,
          }}
        />
        {router.asPath.includes('/tools/video-trimmer') && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${JSON.stringify(
                generateJSONLDSchemaHowTo(HowToSchema, SEOSection.CanonicalURL),
              )}`,
            }}
          />
        )}
      </SEO>
      <LandingPageContainer
        landingpageState={toolsPageState}
        user={user}
        socialLinks={socialLinks}
        allowEditor={allowEditor}
        browser={browser}
        platform={platform}
      />
    </ToolLandingLayout>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let toolsPageState = null;
  const lang = getLanguage(ctx.locale);
  const slug = getSlug(ctx.query.id, ctx.locale);

  const allowedPathsForEditor = ['video-trimmer', 'talking-avatar'];

  try {
    toolsPageState = await getToolsPage(slug, lang);

    if (toolsPageState) {
      if (Array.isArray(toolsPageState.invideo_blogs)) {
        toolsPageState.invideo_blogs = filterBlogResponseForCards(
          toolsPageState.invideo_blogs,
        );
      }
      return {
        props: {
          toolsPageState,
          allowEditor: allowedPathsForEditor.includes(slug),
        },
      };
    }

    return {
      notFound: true,
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
}

function getLanguage(locale?: string) {
  if (locale === 'jp') {
    return 'ja';
  }

  if (locale === 'kr') {
    return 'ko';
  }

  return locale || 'en';
}

function getSlug(queryId: string | string[] | undefined, locale?: string) {
  if (!locale || locale === 'en') {
    return queryId as string;
  }
  return `${locale}-${queryId}`;
}

export default ToolLandingPage;
